import { useEffect, useState, useCallback } from "react";
import Select from "react-select";
import { MaterialReactTable } from "material-react-table";
import api from "../Axios/api";
import Loader from "../common/Loader";
import { FileUp } from "lucide-react";
import * as XLSX from "xlsx";
const DEFAULT_FACTORY_ID = "666c0f5ebeca5ab2c65b2c85";

const factoryOptions = [
    {
        value: "666c0f5ebeca5ab2c65b2c85",
        label: "PolyBond Insulation",
    },
    {
        value: "666c0f5ebeca5ab2c65b2c86",
        label: "PolyBond Project",
    },
    {
        value: "666c0f5ebeca5ab2c65b2c87",
        label: "PolyBond RockFibre",
    },
];

const allCategoryOption = {
    value: "",
    label: "All Categories",
};

const formatNumber = (value, digits = 3) =>
    Number(value ?? 0).toLocaleString("en-IN", {
        maximumFractionDigits: digits,
    });

const formatAmount = (value) =>
    Number(value ?? 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const StockSummaryReport = () => {
    const [selectedFactory, setSelectedFactory] = useState(
        factoryOptions.find(
            (factory) => factory.value === DEFAULT_FACTORY_ID
        )
    );

    const [selectedCategory, setSelectedCategory] =
        useState(allCategoryOption);

    const [categoryOptions, setCategoryOptions] = useState([
        allCategoryOption,
    ]);

    const getCurrentMonthRange = () => {
        const now = new Date();

        // Get current date and time in IST
        const istDate = new Date(
            now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
        );

        const year = istDate.getFullYear();
        const month = istDate.getMonth();

        const formatDate = (date) => {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, "0");
            const d = String(date.getDate()).padStart(2, "0");

            return `${y}-${m}-${d}`;
        };

        return {
            fromDate: formatDate(new Date(year, month, 1)),
            toDate: formatDate(new Date(year, month + 1, 0)),
        };
    };

    const initialDates = getCurrentMonthRange();

    const [fromDate, setFromDate] = useState(initialDates.fromDate);
    const [toDate, setToDate] = useState(initialDates.toDate);


    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loading, setLoading] = useState(false);

    const [stockData, setStockData] = useState([]);


    // Fetch categories
    const fetchItemCategories = useCallback(async () => {
        setLoadingCategories(true);

        try {
            const response = await api.get(
                "/purchase-item-category/get-name"
            );

            const data = Array.isArray(response.data)
                ? response.data
                : response.data?.data || [];

            const options = data.map((category) => ({
                value: category._id,
                label: category.itemcat_title,
            }));

            setCategoryOptions([
                allCategoryOption,
                ...options,
            ]);
        } catch (error) {
            console.error("Error fetching categories:", error);
            setCategoryOptions([allCategoryOption]);
        } finally {
            setLoadingCategories(false);
        }
    }, []);

    // Fetch stock register report
    const fetchissueRegister = useCallback(async () => {
        if (fromDate && toDate && fromDate > toDate) {
            setStockData([]);

            return;
        }

        setLoading(true);

        try {
            const response = await api.get("/inventory/issueRegister", {
                params: {
                    factory: selectedFactory?.value || DEFAULT_FACTORY_ID,
                    category: selectedCategory?.value || "",
                    fromDate: fromDate || "",
                    toDate: toDate || "",
                },
            });
            setStockData(
                Array.isArray(response.data?.data)
                    ? response.data.data
                    : []
            );

            const result = response.data;
            setStockData(
                Array.isArray(result?.data) ? result.data : []
            );


        } catch (error) {
            console.error("Stock register API error:", error);

            setStockData([]);

        } finally {
            setLoading(false);
        }
    }, [
        selectedFactory,
        selectedCategory,
        fromDate,
        toDate,
    ]);

    // Load categories once
    useEffect(() => {
        fetchItemCategories();
    }, [fetchItemCategories]);

    // Reload report whenever a filter changes
    useEffect(() => {
        fetchissueRegister();
    }, [fetchissueRegister]);

    // Factory change
    const handleFactoryChange = (option) => {
        setSelectedFactory(option);
        setSelectedCategory(allCategoryOption);
    };


    const exportToExcel = (data) => {
        if (!data || data.length === 0) {
            alert("No data available to export");
            return;
        }

        const excelData = data.map((item, index) => ({
            "S.No.": index + 1,
            "Voucher No.": item.IssueVoucherNo || "-",
            "Issue Date": item.IssueDate
                ? new Date(item.IssueDate).toLocaleDateString("en-IN")
                : "-",
            "Item Name": item.issueData?.itemName || "-",
            "Factory Name": item.factoryName || "-",
            "Issued Qty": Number(item.issueData?.IssuedQty || 0),
            "Item Unit": item.issueData?.itemUnit || "-",
            "Issued Amount": Number(item.issueData?.IssuedAmt || 0),
            "Issue To": item.IssueTo || "-",
            "Issue Tag": item.IssueTag || "-",
            "Source Type": item.SourceType || "-",
            "Place of Use": item.issueData?.PlaceofUse || "-",
            "Item Remark": item.issueData?.ItemRemark || "-",
            "Issue Remark": item.IssueRemark || "-",
        }));


        const worksheet = XLSX.utils.json_to_sheet(excelData);

        worksheet["!cols"] = [
            { wch: 8 },
            { wch: 25 },
            { wch: 22 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 30 },
            { wch: 30 },
            { wch: 30 },
        ];

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Issue Register"
        );

        XLSX.writeFile(
            workbook,
            `Issue_Register_${new Date().toISOString().slice(0, 10)}.xlsx`
        );
    };

    // Format date without changing the date from the API response
    const formatDate = (value) => {
        if (!value) return "-";

        const date = String(value).split("T")[0];
        const [year, month, day] = date.split("-");

        return `${day}-${month}-${year}`;
    };

    const columns = [
        {
            id: "serialNumber",
            header: "S.No.",
            Cell: ({ row }) => row.index + 1,
            size: 70,
            enableSorting: false,
        },
        {
            accessorKey: "IssueVoucherNo",
            header: "Voucher No.",
            size: 160,
        },
        {
            accessorKey: "IssueDate",
            header: "Issue Date",
            Cell: ({ cell }) => formatDate(cell.getValue()),
            size: 120,
        },


        {
            id: "itemName",
            header: "Item Name",
            accessorFn: (row) => row.issueData?.itemName ?? "-",
            size: 220,
        },
        {
            accessorKey: "factoryName",
            header: "Factory",
            size: 200,
        },

        {
            id: "IssuedQty",
            header: "Issued Qty",
            accessorFn: (row) => Number(row.issueData?.IssuedQty ?? 0),
            Cell: ({ row, cell }) => (
                <span className="whitespace-nowrap">
                    {Number(cell.getValue()).toLocaleString("en-IN", {
                        maximumFractionDigits: 3,
                    })}{" "}
                    <span className="text-xs font-semibold text-red-500">
                        {row.original.issueData?.itemUnit ?? ""}
                    </span>
                </span>
            ),
            size: 120,
        },
        {
            id: "IssuedAmt",
            header: "Issued Amount",
            accessorFn: (row) => Number(row.issueData?.IssuedAmt ?? 0),
            Cell: ({ cell }) =>
                Number(cell.getValue()).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }),
            size: 150,
        },
        {
            accessorKey: "IssueTo",
            header: "Issue To",
            size: 140,
        },
        {
            accessorKey: "SourceType",
            header: "Source Type",
            size: 130,
        },
        {
            accessorKey: "IssueTag",
            header: "Issue Tag",
            size: 140,
        },
        {
            id: "PlaceofUse",
            header: "Place of Use",
            accessorFn: (row) => row.issueData?.PlaceofUse ?? "-",
            size: 140,
        },
        {
            id: "ItemRemark",
            header: "Item Remark",
            accessorFn: (row) => row.issueData?.ItemRemark || "-",
            size: 180,
        },
        {
            accessorKey: "IssueRemark",
            header: "Issue Remark",
            Cell: ({ cell }) => cell.getValue() || "-",
            size: 180,
        },
    ];


    return (
        <div className="min-h-screen bg-gray-50 p-5">
            <h1 className="mb-5 text-2xl font-bold text-gray-800">
                Consumption Register
            </h1>

            {/* Filters */}
            <div className="mb-5 grid grid-cols-1 gap-4 rounded-xl bg-white p-4 shadow-sm md:grid-cols-2 xl:grid-cols-4">
                {/* Factory */}

                <div className="relative">
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                        Select Factory
                    </label>

                    <Select
                        options={factoryOptions}
                        value={selectedFactory}
                        onChange={handleFactoryChange}
                        placeholder="Select factory"
                        isClearable={false}
                        menuPortalTarget={
                            typeof document !== "undefined" ? document.body : undefined
                        }
                        menuPosition="fixed"
                        styles={{
                            menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                            }),
                            menu: (base) => ({
                                ...base,
                                zIndex: 9999,
                            }),
                        }}
                    />
                </div>

                {/* Category */}
                <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                        Select Category
                    </label>

                    <Select
                        options={categoryOptions}
                        value={selectedCategory}
                        onChange={(option) =>
                            setSelectedCategory(
                                option || allCategoryOption
                            )
                        }
                        placeholder="Select category"
                        isClearable={false}
                        isLoading={loadingCategories}
                        menuPortalTarget={
                            typeof document !== "undefined"
                                ? document.body
                                : undefined
                        }
                        menuPosition="fixed"
                        styles={{
                            menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                            }),
                        }}
                    />
                </div>

                {/* From Date */}
                <div>
                    <label
                        htmlFor="fromDate"
                        className="mb-2 block text-sm font-medium text-gray-700"
                    >
                        From Date
                    </label>

                    <input
                        id="fromDate"
                        name="fromDate"
                        type="date"
                        value={fromDate}
                        max={toDate || undefined}
                        onChange={(event) =>
                            setFromDate(event.target.value)
                        }
                        className="w-full rounded-md border border-gray-300 p-2.5 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                </div>

                {/* To Date */}
                <div>
                    <label
                        htmlFor="toDate"
                        className="mb-2 block text-sm font-medium text-gray-700"
                    >
                        To Date
                    </label>

                    <input
                        id="toDate"
                        name="toDate"
                        type="date"
                        value={toDate}
                        min={fromDate || undefined}
                        onChange={(event) =>
                            setToDate(event.target.value)
                        }
                        className="w-full rounded-md border border-gray-300 p-2.5 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                </div>



            </div>
            <button
                onClick={() => exportToExcel(stockData)}
                className="inline-flex gap-2 mb-2 bg-green-600 hover:bg-green-700 active:scale-95 text-white px-2 py-2.5 rounded-lg font-medium text-sm shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
                <FileUp size={18} strokeWidth={2} />
                Export
            </button>

            {/* Stock Register Table */}
            <div className="rounded-xl bg-white p-4 shadow-sm">

                {loading ? (
                    <Loader />
                ) : (
                    <MaterialReactTable
                        columns={columns}
                        data={stockData}
                        enablePagination
                        enableSorting
                        enableGlobalFilter
                        enableColumnFilters={false}
                        enableDensityToggle={false}
                        enableFullScreenToggle
                        enableHiding
                        enableStickyHeader
                        initialState={{
                            pagination: {
                                pageIndex: 0,
                                pageSize: 10,
                            },
                            density: "comfortable",
                        }}
                        muiPaginationProps={{
                            rowsPerPageOptions: [
                                10,
                                25,
                                50,
                                100,
                            ],
                            showFirstButton: true,
                            showLastButton: true,
                        }}
                        muiTableContainerProps={{
                            sx: {
                                maxHeight: "600px",
                            },
                        }}
                        muiTablePaperProps={{
                            elevation: 0,
                            sx: {
                                borderRadius: "8px",
                                overflow: "hidden",
                            },
                        }}
                        muiTableHeadCellProps={{
                            sx: {
                                backgroundColor: "#f9fafb",
                                color: "#4b5563",
                                fontSize: "12px",
                                fontWeight: 600,
                                borderBottom: "1px solid #e5e7eb",
                                whiteSpace: "nowrap",
                            },
                        }}
                        muiTableBodyCellProps={{
                            sx: {
                                fontSize: "13px",
                                color: "#374151",
                                py: 1.5,
                                whiteSpace: "nowrap",
                            },
                        }}
                        renderEmptyRowsFallback={() => (
                            <div className="w-full py-8 text-center text-sm text-gray-500">
                                No stock records found.
                            </div>
                        )}
                    />

                )}

            </div>
        </div>
    );
};

export default StockSummaryReport;